import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Warms up route chunks in the background so navigation feels instant
 * without hurting initial FCP/LCP.
 *
 * Strategy:
 *  - Wait until the browser is idle (requestIdleCallback) after first paint.
 *  - Prefetch the highest-intent public routes for each context (homepage,
 *    catalog pages, blog, checkout funnel).
 *  - Skip on Save-Data / slow connections so we don't waste user bandwidth.
 */

const idle = (cb: () => void, timeout = 3000) => {
  if (typeof window === "undefined") return;
  const ric: any = (window as any).requestIdleCallback;
  if (ric) ric(cb, { timeout });
  else setTimeout(cb, 1200);
};

const shouldSkip = () => {
  if (typeof navigator === "undefined") return true;
  const conn = (navigator as any).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return true;
  return false;
};

// Prefetchers per route context. Each entry is an arrow returning the
// dynamic import so Vite/Rollup emits a discrete chunk we can warm.
const PREFETCH_MAP: Record<string, Array<() => Promise<unknown>>> = {
  home: [
    () => import("@/pages/Equipment"),
    () => import("@/pages/EquipmentCategory"),
    () => import("@/pages/Consumables"),
    () => import("@/pages/Shop"),
    () => import("@/pages/Contact"),
    () => import("@/pages/Blog"),
  ],
  equipamentos: [
    () => import("@/pages/EquipmentCategory"),
    () => import("@/pages/EquipmentDetail"),
    () => import("@/pages/Consumables"),
  ],
  consumiveis: [
    () => import("@/pages/ConsumablesCategory"),
    () => import("@/pages/ConsumableDetail"),
    () => import("@/pages/Shop"),
  ],
  shop: [
    () => import("@/pages/Cart"),
    () => import("@/pages/Checkout"),
    () => import("@/pages/Auth"),
  ],
  cart: [
    () => import("@/pages/Checkout"),
    () => import("@/pages/Auth"),
  ],
  blog: [() => import("@/pages/BlogArticle")],
  landing: [
    () => import("@/pages/EquipmentCategory"),
    () => import("@/pages/EquipmentDetail"),
    () => import("@/pages/Contact"),
  ],
};

const bucketFor = (pathname: string): keyof typeof PREFETCH_MAP | null => {
  // Strip language prefix
  const p = pathname.replace(/^\/(en|es|pl|fr)(?=\/|$)/, "") || "/";
  if (p === "/" || p === "") return "home";
  if (/^\/(equipamentos|equipment|equipos|sprzet|equipements|equipamento|equipment-detail)/.test(p))
    return "equipamentos";
  if (/^\/(consumiveis|consumables|consumibles|materialy-eksploatacyjne|consommables|consumivel|consumable)/.test(p))
    return "consumiveis";
  if (/^\/(loja|shop|tienda|sklep|boutique)/.test(p)) return "shop";
  if (/^\/(carrinho|cart|carrito|koszyk|panier)/.test(p)) return "cart";
  if (/^\/blog/.test(p)) return "blog";
  if (/^\/(dtf|impressoras?-uv|impressora-sublimacao|sublimacao|prensa-termica|prensas-termicas|plotter-corte|plotter-de-corte|calandras?|laser-co2|maquina-laser-co2|solucoes|solutions|financiamento|financing)/.test(p))
    return "landing";
  return null;
};

const warmed = new Set<() => Promise<unknown>>();

export function RoutePrefetcher() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (shouldSkip()) return;
    const bucket = bucketFor(pathname);
    if (!bucket) return;
    const jobs = PREFETCH_MAP[bucket];
    if (!jobs?.length) return;

    let cancelled = false;
    idle(() => {
      if (cancelled) return;
      // Run sequentially with micro-delays so we don't fight the main route's
      // network priority.
      let i = 0;
      const step = () => {
        if (cancelled || i >= jobs.length) return;
        const job = jobs[i++];
        if (!warmed.has(job)) {
          warmed.add(job);
          job().catch(() => warmed.delete(job));
        }
        idle(step, 2000);
      };
      step();
    });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}

export default RoutePrefetcher;
